<template>
  <EditorUiConfigPanel :expanded="state.expanded" @toggle="togglePanelVisibility">
    <template #header>
      <div class="flex items-center">
        <p class="mb-0 mr-2 text-xs font-medium">Slide-level data configuration</p>
        <a
          v-if="userState.flags.MANAGE_DRILL_LABELS_ENABLED"
          class="flex w-4 h-4 p-0 text-white bg-transparent hover_text-gray-600"
          title="Edit master data config"
          data-testid="file-manager-btn"
          :href="fileManagerUrl"
          target="_blank"
        >
          <ArrowTopRightOnSquareIcon class="w-6" />
        </a>
      </div>
    </template>

    <template #default>
      <div class="space-y-4 py-3.5">
        <div class="flex">
          <EditorDeckSlidesThreeDDrillDataConfigControl>
            <template #button>
              <VButton
                class="mr-2"
                :variant="state.allHighlightLabelDataClicked ? 'copper' : 'dark-gray-borderless'"
                size="xs"
                @click="state.allHighlightLabelDataClicked = !state.allHighlightLabelDataClicked"
              >
                <AdjustmentsVerticalIcon class="w-4 h-4" />
              </VButton>
            </template>
            <p class="text-sm">All Highlight Label Data</p>
          </EditorDeckSlidesThreeDDrillDataConfigControl>

          <div
            v-if="state.allHighlightLabelDataClicked"
            ref="labelSizeMenu"
            class="absolute flex h-10 bg-gray-900 rounded-lg shadow -left-72"
          >
            <EditorLabelSizeMenu v-if="!isAutoScale" :value="size" @input="handleSizeChange" />
            <div v-else class="flex flex-grow min-w-0">
              <EditorFontSizeSelector :value="size" :options="fontOptions" @input="handleSizeChange" />
            </div>

            <button
              class="flex items-center justify-center px-4 text-xs text-white border-l border-transparent gap-2 rounded-r-md box-border"
              :class="isAutoScale ? 'bg-copper-800 hover_bg-copper-900' : 'bg-gray-900 border-gray-800 hover_bg-gray-800'"
              @click="toggleAutoScale"
            >
              <span class="overflow-visible text-sm rounded-none"><FontAwesomeIcon icon="up-right-and-down-left-from-center" /></span>
              Autoscale on zoom
            </button>
          </div>
        </div>
      </div>
    </template>
  </EditorUiConfigPanel>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { ref, reactive } from 'vue'
import { ArrowTopRightOnSquareIcon } from '@heroicons/vue/24/outline'
import { AdjustmentsVerticalIcon } from '@heroicons/vue/24/solid'

type State = {
  expanded: boolean
  allHighlightLabelDataClicked: boolean
}

defineProps({
  size: {
    type: Number as PropType<number>,
    required: true,
  },
  fontOptions: {
    type: Array as PropType<DeepReadonly<{ id: number; name: string; classes: string }[]>>,
    required: true,
  },
  fileManagerUrl: {
    type: String as PropType<string>,
    required: true,
  },
  isAutoScale: {
    type: Boolean as PropType<boolean>,
    required: true,
  },
})

const emit = defineEmits(['sizeChange', 'toggleAutoScale'])

const state = reactive<State>({
  expanded: true,
  allHighlightLabelDataClicked: false,
})

const labelSizeMenu = ref<HTMLElement | undefined>()

const { state: userState } = useUser()
useClickOutside([labelSizeMenu], ref(false), () => (state.allHighlightLabelDataClicked = false))

function togglePanelVisibility() {
  state.expanded = !state.expanded
}

function toggleAutoScale() {
  emit('toggleAutoScale')
}

function handleSizeChange(size: number) {
  emit('sizeChange', size)
}
</script>
